import { Link } from 'react-router-dom';
import RevealOnScroll from '../../components/base/RevealOnScroll';

const ABOUT_IMAGE = 'https://public.readdy.ai/ai/img_res/ce4160cc363987b611f2a6be49968ae0.jpg';
const STORY_IMAGE = 'https://readdy.ai/api/search-image?query=skilled%20roofing%20crew%20completing%20premium%20shingle%20roof%20installation%20on%20classic%20chicago%20bungalow%2C%20multiple%20workers%20in%20safety%20harnesses%20working%20together%20on%20steep%20pitch%20roof%2C%20proud%20craftsmanship%20team%20on%20completed%20residential%20roofing%20project%2C%20golden%20hour%20light%2C%20professional%20construction%20photography&width=800&height=1000&seq=gwabout002&orientation=portrait';
const PHONE_HREF = 'tel:7086984072';

const values = [
  {
    icon: 'ri-hand-heart-line',
    title: 'Family Accountability',
    desc: "When you call us, you reach the Christo family directly. No call centers, no corporate layers — just the people doing the actual work.",
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Full Transparency',
    desc: "Every project starts with a complete inspection and a written estimate. You know what's being done, what materials are used, and what it costs — before we start.",
  },
  {
    icon: 'ri-award-line',
    title: 'Quality Over Volume',
    desc: "We don't rush jobs to squeeze in more work. Every project gets the attention it deserves. We'd rather do fewer jobs and do them right.",
  },
  {
    icon: 'ri-snowflake-line',
    title: 'Chicago-Specific Expertise',
    desc: "Midwest roofing is different. We've spent 35+ years learning what materials and installation techniques actually hold up in Chicago's climate extremes.",
  },
];

const stats = [
  { val: '1989', label: 'Year Founded' },
  { val: '35+', label: 'Years Serving Chicago' },
  { val: '5,000+', label: 'Completed Projects' },
  { val: '24+', label: 'Cities Served' },
];

export default function AboutPage() {
  return (
    <main>
      {/* Hero */}
      <section
        className="relative flex items-end min-h-[440px] pt-40 pb-16 overflow-hidden"
        style={{ backgroundImage: `url('${ABOUT_IMAGE}')`, backgroundSize: 'cover', backgroundPosition: 'center 30%' }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/60 to-black/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <div className="inline-flex items-center gap-2 bg-brand-gold/20 border border-brand-gold/40 rounded-full px-4 py-1.5 mb-5">
            <i className="ri-home-heart-line text-brand-gold text-xs" />
            <span className="text-brand-gold text-xs font-bold uppercase tracking-widest">About Green World</span>
          </div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
            Built on Craftsmanship.<br />
            <span className="text-brand-gold italic">Focused on Results.</span>
          </h1>
          <p className="text-white/75 max-w-xl leading-relaxed">
            Chicago&apos;s family-owned roofing and construction company since 1989. We started with flat roofs
            and now deliver complete exterior solutions for homes and businesses throughout Chicagoland.
          </p>
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-brand-dark py-5 border-b border-white/5">
        <div className="h-0.5 bg-gradient-to-r from-transparent via-brand-gold/40 to-transparent mb-0" />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-5 flex flex-wrap items-center justify-center gap-6 lg:gap-10">
          {['Local Expertise Since 1989', 'Family-Owned & Operated', 'Licensed & Insured', 'Proven Chicago Results'].map((badge) => (
            <span key={badge} className="flex items-center gap-1.5 text-xs text-gray-400 whitespace-nowrap">
              <i className="ri-shield-check-fill text-brand-gold text-xs" />
              {badge}
            </span>
          ))}
        </div>
      </section>

      {/* Stats bar */}
      <section className="bg-brand-green py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <RevealOnScroll key={stat.label} delay={idx * 100} className="text-center">
                <div className="font-display text-4xl lg:text-5xl font-bold text-brand-gold">{stat.val}</div>
                <div className="text-white/75 text-sm mt-2">{stat.label}</div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="bg-brand-cream py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <RevealOnScroll direction="left">
              <span className="text-brand-green text-xs font-bold uppercase tracking-widest inline-flex items-center gap-2 mb-4">
                <span className="w-8 h-px bg-brand-green inline-block" />
                Our Story
              </span>
              <h2 className="font-display text-4xl font-bold text-brand-dark mb-6 leading-tight">
                Three Decades of Protecting<br />
                <span className="text-brand-green italic">Chicago Homes &amp; Businesses</span>
              </h2>
              <div className="flex flex-col gap-4 text-gray-600 leading-relaxed text-sm">
                <p>
                  Green World Construction is family owned and operated. We have been serving Chicago and the
                  Chicagoland suburbs since 1989. Current owner and manager Wayne Christo continues the work
                  his father and siblings began building more than three decades ago.
                </p>
                <p>
                  We started with flat roofs and now specialize in all types of roofing systems: shingles,
                  tuckpointing, masonry, asphalt paving, customized sheet metal, concrete, and general
                  exterior contracting. Every trade we offer was added because our customers needed it —
                  and we refused to send them elsewhere.
                </p>
                <p>
                  We conduct a complete inspection of every customer&apos;s situation, accurately provide the
                  information needed, and offer a written estimate and timeline before any work begins.
                  We take great pride in keeping customers informed from start to finish.
                </p>
                <p>
                  This is not a large corporation with rotating crews and anonymous project managers. When
                  you call Green World, you reach the same family that has been doing this work in your
                  Chicago neighborhood for over 35 years.
                </p>
              </div>
            </RevealOnScroll>

            <RevealOnScroll direction="right" delay={100} className="relative">
              <div className="w-full h-[440px] rounded-2xl overflow-hidden">
                <img
                  src={STORY_IMAGE}
                  alt="Green World Construction roofing crew in Chicago"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-5 -left-5 bg-brand-dark text-white p-6 rounded-2xl">
                <div className="text-xs text-brand-gold font-bold uppercase tracking-widest mb-1">Serving Chicago</div>
                <div className="font-display text-3xl font-bold">Since 1989</div>
              </div>
            </RevealOnScroll>
          </div>

          {/* Values */}
          <div>
            <RevealOnScroll className="text-center mb-10">
              <span className="text-brand-green text-xs font-bold uppercase tracking-widest">Our Values</span>
              <h2 className="font-display text-3xl font-bold text-brand-dark mt-3">What Sets Us Apart</h2>
            </RevealOnScroll>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {values.map((val, idx) => (
                <RevealOnScroll key={val.title} delay={idx * 80}>
                  <div className="bg-white rounded-xl p-7 border border-brand-cream-3 hover:border-brand-gold/40 transition-all h-full">
                    <div className="w-12 h-12 flex items-center justify-center bg-brand-green/10 rounded-xl mb-5">
                      <i className={`${val.icon} text-brand-green text-xl`} />
                    </div>
                    <h3 className="font-bold text-brand-dark mb-2.5 text-sm">{val.title}</h3>
                    <p className="text-xs text-gray-500 leading-relaxed">{val.desc}</p>
                  </div>
                </RevealOnScroll>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AI Answer Block */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <RevealOnScroll className="text-center mb-10">
            <h2 className="font-display text-3xl font-bold text-brand-dark">About Green World — Common Questions</h2>
          </RevealOnScroll>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto">
            {[
              { q: 'Who is Green World Construction?', a: 'Green World Construction and Roofing is a professional roofing and exterior construction company based at 5801 N Northwest Highway, Chicago, IL. Family-owned by the Christo family since 1989, we specialize in all roofing systems, tuckpointing, masonry, siding, gutters, and sealcoating.' },
              { q: 'What makes the company different?', a: 'A focus on durability, design precision, and long-term value — combined with direct family accountability. You deal with the same team from inspection to final walkthrough. No subcontractors, no corporate layers, no surprise costs.' },
              { q: 'What services does Green World offer?', a: 'We offer flat roofing, shingle roofing, commercial roofing, tuckpointing, masonry, chimney repair, sheet metal fabrication, seamless gutters, vinyl siding, and asphalt sealcoating — everything your Chicago property\'s exterior needs.' },
              { q: "What is the company's mission?", a: 'To deliver roofing and exterior construction that combines long-term performance, transparent communication, and genuine craftsmanship. We build reputations one roof at a time — and have been doing it in Chicago for 35+ years.' },
            ].map((item, idx) => (
              <RevealOnScroll key={idx} delay={idx * 70}>
                <div className="bg-brand-cream rounded-xl p-6 border border-brand-cream-3 hover:border-brand-green/30 transition-all h-full">
                  <h3 className="font-bold text-brand-dark mb-3 text-sm flex items-start gap-2">
                    <i className="ri-question-line text-brand-green text-sm mt-0.5 flex-shrink-0" />
                    {item.q}
                  </h3>
                  <p className="text-xs text-gray-600 leading-relaxed">{item.a}</p>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Services overview */}
      <section className="bg-brand-cream py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <RevealOnScroll className="text-center mb-8">
            <h2 className="font-display text-3xl font-bold text-brand-dark">Full-Service Exterior Solutions</h2>
            <p className="text-gray-500 mt-3 max-w-xl mx-auto text-sm">
              Everything your Chicago property&apos;s exterior needs — under one roof, from one team.
            </p>
          </RevealOnScroll>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {['Flat Roofing', 'Shingle Roofing', 'Commercial Roofing', 'Tuckpointing', 'Chimney Repair', 'Sheet Metal & Gutters', 'Vinyl Siding', 'Sealcoating'].map((service, idx) => (
              <RevealOnScroll key={service} delay={idx * 50}>
                <div className="bg-white rounded-xl p-4 flex items-center gap-3 border border-brand-cream-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-double-line text-brand-green text-base" />
                  </div>
                  <span className="text-sm text-gray-700 font-medium">{service}</span>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-brand-dark py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'repeating-linear-gradient(45deg, #c9a227 0, #c9a227 1px, transparent 0, transparent 20px)', backgroundSize: '28px 28px' }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <RevealOnScroll className="text-center mb-10">
            <h2 className="font-display text-3xl font-bold text-white">Our Process</h2>
          </RevealOnScroll>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { step: '01', label: 'Consultation', desc: 'We listen to your concerns and schedule a site visit.' },
              { step: '02', label: 'Inspection', desc: 'Complete assessment of your roof and exterior.' },
              { step: '03', label: 'Written Estimate', desc: 'Detailed cost and timeline before work begins.' },
              { step: '04', label: 'Installation', desc: 'Expert crew on-site, managed from start to finish.' },
              { step: '05', label: 'Completion', desc: 'Final walkthrough and quality confirmation.' },
            ].map((item, idx) => (
              <RevealOnScroll key={item.step} delay={idx * 80}>
                <div className="bg-white/5 border border-white/10 rounded-xl p-5 text-center hover:border-brand-gold/30 transition-all">
                  <div className="w-12 h-12 flex items-center justify-center bg-brand-gold/10 border border-brand-gold/25 rounded-full mx-auto mb-3">
                    <span className="font-display text-brand-gold font-bold">{item.step}</span>
                  </div>
                  <h3 className="text-white font-bold text-sm mb-1">{item.label}</h3>
                  <p className="text-gray-400 text-xs leading-relaxed">{item.desc}</p>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-brand-cream py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <RevealOnScroll className="text-center mb-8">
            <h2 className="font-display text-3xl font-bold text-brand-dark">Experienced Team. High-End Execution.</h2>
          </RevealOnScroll>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-4xl mx-auto">
            {[
              { icon: 'ri-history-line', t: '35+ Years Experience', d: 'Deep Chicago roofing expertise since 1989.' },
              { icon: 'ri-shield-check-line', t: 'Licensed & Fully Insured', d: 'All Illinois credentials maintained.' },
              { icon: 'ri-home-heart-line', t: 'Family-Owned & Operated', d: 'Direct accountability, no middlemen.' },
              { icon: 'ri-file-list-3-line', t: 'Written Estimates Always', d: 'No verbal quotes, no hidden costs.' },
              { icon: 'ri-snowflake-line', t: 'Midwest Climate Experts', d: 'Materials rated for Chicago conditions.' },
              { icon: 'ri-communication-line', t: 'Client-Focused Approach', d: 'Informed at every stage of the project.' },
            ].map((item, idx) => (
              <RevealOnScroll key={item.t} delay={idx * 60}>
                <div className="bg-white rounded-xl p-5 border border-brand-cream-3 flex gap-4">
                  <div className="w-9 h-9 flex items-center justify-center bg-brand-green/10 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-brand-green text-base`} />
                  </div>
                  <div>
                    <h3 className="font-bold text-brand-dark text-sm mb-1">{item.t}</h3>
                    <p className="text-xs text-gray-500">{item.d}</p>
                  </div>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="bg-white py-14">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <RevealOnScroll className="text-center mb-7">
            <h2 className="font-display text-2xl font-bold text-brand-dark">Serving Communities Across Illinois</h2>
            <p className="text-gray-500 text-sm mt-2">Serving homeowners and businesses throughout Chicago and 24+ Chicagoland suburbs.</p>
          </RevealOnScroll>
          <RevealOnScroll>
            <div className="bg-brand-cream rounded-xl p-6 text-center border border-brand-cream-3">
              <p className="text-gray-600 text-sm leading-loose">
                Chicago &bull; Evanston &bull; Skokie &bull; Oak Park &bull; Park Ridge &bull; Northbrook &bull; Berwyn &bull; Elmhurst &bull; Wheeling &bull; Elmwood Park &bull; Forest Park &bull; Franklin Park &bull; Norridge &bull; Barrington &bull; Cicero &bull; Burbank &bull; Harwood Heights &bull; Lincolnwood &bull; Melrose Park &bull; Schiller Park &bull; and more
              </p>
              <Link to="/locations" className="inline-flex items-center gap-1.5 text-brand-green text-sm font-bold mt-4 hover:underline cursor-pointer">
                View All Service Areas <i className="ri-arrow-right-line text-xs" />
              </Link>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* Testimonials snippet */}
      <section className="bg-brand-dark py-14">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <RevealOnScroll className="text-center mb-7">
            <h2 className="font-display text-2xl font-bold text-white">What Clients Value Most</h2>
          </RevealOnScroll>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: 'ri-star-fill', text: 'Professionalism & clear communication throughout' },
              { icon: 'ri-shield-check-fill', text: 'Quality of work and attention to detail' },
              { icon: 'ri-home-gear-fill', text: 'Transparent pricing and no surprise costs' },
            ].map((item, idx) => (
              <RevealOnScroll key={item.text} delay={idx * 80}>
                <div className="bg-white/8 border border-white/10 rounded-xl p-5 text-center">
                  <div className="w-10 h-10 flex items-center justify-center bg-brand-gold/15 rounded-full mx-auto mb-3">
                    <i className={`${item.icon} text-brand-gold text-lg`} />
                  </div>
                  <p className="text-white/80 text-xs leading-relaxed">{item.text}</p>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-brand-cream py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <RevealOnScroll>
            <h2 className="font-display text-3xl font-bold text-brand-dark text-center mb-8">About Green World — FAQ</h2>
          </RevealOnScroll>
          <div className="flex flex-col gap-4">
            {[
              { q: 'Who is the team behind Green World Construction?', a: 'Green World Construction is led by the Christo family. Wayne Christo manages the company, continuing the work his father and siblings started in 1989. Our team consists of experienced roofing and construction professionals who have been working in Chicago and the surrounding suburbs for decades.' },
              { q: 'What type of projects does Green World handle?', a: "We handle projects ranging from small roof repairs to complete roof replacements, full tuckpointing and masonry restoration, commercial roofing systems, vinyl siding installation, gutter replacement, chimney repair, and asphalt sealcoating. Both residential and commercial clients." },
              { q: 'How do I get started with Green World?', a: "Call us at (708) 698-4072 or fill out our contact form to request a free inspection. We'll schedule an on-site assessment, provide a complete written report of findings, and give you a detailed estimate — all at no charge and with no obligation." },
            ].map((item, idx) => (
              <RevealOnScroll key={idx} delay={idx * 70}>
                <div className="bg-white rounded-xl p-7 border border-brand-cream-3">
                  <h3 className="font-bold text-brand-dark mb-3 text-sm flex items-start gap-2">
                    <i className="ri-question-line text-brand-green text-sm mt-0.5 flex-shrink-0" />
                    {item.q}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.a}</p>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-green py-16">
        <RevealOnScroll className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl font-bold text-white mb-3">Let&apos;s Build Something Exceptional</h2>
          <p className="text-white/75 mb-8">Contact Green World today to begin your project with Chicago&apos;s most trusted family-owned roofing company.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact" className="bg-brand-gold hover:bg-brand-gold-dark text-white text-sm font-bold px-10 py-4 rounded-md transition-colors cursor-pointer whitespace-nowrap">
              Contact Us Today
            </Link>
            <a href={PHONE_HREF} className="border-2 border-white/40 hover:border-white text-white text-sm font-bold px-10 py-4 rounded-md transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
              <i className="ri-phone-fill" />
              (708) 698-4072
            </a>
          </div>
        </RevealOnScroll>
      </section>
    </main>
  );
}
